/**
 * Goods Issue Note Form — creates (or edits) a draft Goods Issue Note against an
 * approved Goods Transfer Request. Saving here does NOT move any stock — the draft
 * must be submitted and then approved (see GoodsIssueNoteList) before the stock
 * transfer actually executes.
 */

import React, { useState } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';
import toast from 'react-hot-toast';

const GoodsIssueNoteForm = ({ gtr, gin, onClose, onSuccess }) => {
  const { createGoodsIssueNote, updateGoodsIssueNote } = useInventory();
  const isEditMode = !!gin;

  // Both a GTR line item and a GIN line item carry the same display/ceiling fields
  // (itemDescription/itemCode/assetName/assetCode/quantityRequested/quantityApproved) —
  // only the identifying key differs: a GTR line's own `_id` becomes `gtrLineItemId`
  // on the GIN; an existing GIN line already stores that same id as `gtrLineItemId`.
  const sourceLineItems = isEditMode ? (gin.lineItems || []) : (gtr.lineItems || []);
  const issuableLines = isEditMode
    ? sourceLineItems
    : sourceLineItems.filter(line => line.quantityApproved > 0);
  const lineKey = (line) => (isEditMode ? line.gtrLineItemId : line._id);

  const [quantities, setQuantities] = useState(() => {
    const initial = {};
    issuableLines.forEach(line => {
      initial[lineKey(line)] = isEditMode ? line.quantityIssued : line.quantityApproved;
    });
    return initial;
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleQuantityChange = (key, value) => {
    setQuantities(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    issuableLines.forEach(line => {
      const key = lineKey(line);
      const qty = parseFloat(quantities[key]);
      if (!qty || qty <= 0) {
        newErrors[key] = 'Required';
      } else if (qty > line.quantityApproved) {
        newErrors[key] = `Max ${line.quantityApproved}`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        lineItems: issuableLines.map(line => ({
          gtrLineItemId: lineKey(line),
          quantityIssued: parseFloat(quantities[lineKey(line)])
        }))
      };

      if (isEditMode) {
        await updateGoodsIssueNote(gin._id, payload);
        toast.success('Goods issue note updated');
      } else {
        await createGoodsIssueNote({ gtrId: gtr._id, ...payload });
        toast.success('Goods issue note saved as draft');
      }

      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save goods issue note';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (hasError) =>
    `w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`;

  const requestNo = isEditMode ? gin.gtrRequestNo : gtr.requestNo;
  const procurementType = isEditMode ? gin.procurementType : gtr.procurementType;
  const sourceWarehouseName = isEditMode ? gin.sourceWarehouseName : gtr.sourceWarehouseName;
  const sourceLocationName = isEditMode ? gin.sourceLocationName : gtr.sourceLocationName;
  const destinationWarehouseName = isEditMode ? gin.destinationWarehouseName : gtr.destinationWarehouseName;
  const destinationLocationName = isEditMode ? gin.destinationLocationName : gtr.destinationLocationName;

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-[60]">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl">

          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">
                  {isEditMode ? `Edit Goods Issue Note — ${gin.issueNoteNo}` : `Convert to Goods Issue Note — ${requestNo}`}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="px-4 pt-3 pb-2">
            {submitError && (
              <div className="mb-2 bg-red-50 border border-red-200 rounded p-2">
                <p className="text-xs text-red-700">{submitError}</p>
              </div>
            )}

            <div className="mb-2 bg-blue-50 border border-blue-200 rounded p-2">
              <p className="text-xs text-blue-700">
                This saves a draft — no stock moves until it is submitted and then approved.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3 text-xs text-gray-600">
              <div>
                <span className="font-medium text-gray-500">Source: </span>
                {sourceWarehouseName} ({sourceLocationName})
              </div>
              <div>
                <span className="font-medium text-gray-500">Destination: </span>
                {destinationWarehouseName} ({destinationLocationName})
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
              <div className="bg-gray-50 px-3 py-1.5">
                <span className="text-xs font-semibold text-gray-700">Line Items</span>
              </div>
              <div className="max-h-[40vh] overflow-y-auto">
                <table className="min-w-max w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">
                        {procurementType === 'FIXED_ASSET' ? 'Fixed Asset' : 'Item'}
                      </th>
                      <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Requested</th>
                      <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Approved</th>
                      <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Issuing Qty *</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {issuableLines.map(line => {
                      const key = lineKey(line);
                      return (
                        <tr key={key}>
                          <td className="px-2 py-1.5">
                            <div className="text-xs font-medium text-gray-900">{line.itemDescription || line.assetName}</div>
                            <div className="text-xs text-gray-500 font-mono">{line.itemCode || line.assetCode}</div>
                          </td>
                          <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.quantityRequested}</td>
                          <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.quantityApproved}</td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min="0.001"
                              max={line.quantityApproved}
                              step="0.001"
                              value={quantities[key] ?? ''}
                              onChange={(e) => handleQuantityChange(key, e.target.value)}
                              className={`${inputCls(!!errors[key])} w-24 text-center mx-auto`}
                            />
                            {errors[key] && (
                              <p className="text-[10px] text-red-500 mt-0.5 text-center">{errors[key]}</p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {submitting && (
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {submitting ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save Draft')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GoodsIssueNoteForm;
